import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LGAutocomplete from "../../../atoms/Common/LGAutocomplete.tsx";
import EmpaqueScene from "./EmpaqueScene";
import { pack, containerDimsFromProps } from "./packing";
import { getFinishedGoodList, getFinishedGoodById } from "../../../api/finishedGoods";
import { getCatalogue } from "../../../api/common/catalogues.ts";
import { useSnack } from "../../../services/SnakbarProvider";
import { handleError } from "../../../services/errorHandler";
import { palletDimsFromProps } from "../../FinishedGoods/palletProps";

/**
 * Parse props and format dim label.
 * @param {object|string|null} props
 */
function formatContainerDims(props) {
  const dims = containerDimsFromProps(props);
  if (!dims) return "Sin dimensiones en Props";
  return `L ${dims.contL} x W ${dims.contW} x H ${dims.contH} cm`;
}

/**
 * Prefill masters from export lines matching finishedGoodId.
 */
function mastersFromExportItems(exportItems, finishedGoodId, piecesPerMasterFallback) {
  if (!finishedGoodId || !Array.isArray(exportItems)) return 0;
  let total = 0;
  for (const item of exportItems) {
    if (Number(item.finishedGoodId) !== Number(finishedGoodId)) continue;
    const ppm =
      Number(item.piecesPerMaster) > 0
        ? Number(item.piecesPerMaster)
        : Number(piecesPerMasterFallback) || 0;
    const qty = Number(item.itemQuantity) || 0;
    if (ppm > 0) {
      total += Math.floor(qty / ppm);
    }
  }
  return total;
}

/**
 * @param {{ exportData: object, companyId: number|string }} props
 */
export default function EmpaquePanel({ exportData, companyId }) {
  const { openSnack } = useSnack();
  const sceneRef = useRef(null);

  const shipmentType = exportData?.shipmentType;
  const shipmentCode = String(shipmentType?.code || "").toUpperCase();

  const [containerOptions, setContainerOptions] = useState([]);
  const [containerType, setContainerType] = useState(null);
  const [finishedGood, setFinishedGood] = useState(null);
  const [quantity, setQuantity] = useState(0);
  const [packPlan, setPackPlan] = useState(null);
  const [activeContainer, setActiveContainer] = useState(0);
  const [loadingFg, setLoadingFg] = useState(false);

  const [lineFgOptions, setLineFgOptions] = useState([]);

  // Resolve unique finished goods from export lines (GetById may omit nested FG)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const items = exportData?.exportItems || [];
      const ids = [
        ...new Set(
          items
            .map((i) => i.finishedGoodId)
            .filter((id) => id != null)
            .map(Number)
        ),
      ];
      if (!ids.length) {
        setLineFgOptions([]);
        return;
      }
      try {
        const loaded = await Promise.all(
          ids.map(async (fgId) => {
            const fromNav = items.find(
              (i) => Number(i.finishedGoodId) === fgId && i.finishedGood
            )?.finishedGood;
            if (
              fromNav?.masterWidth != null &&
              fromNav?.masterHeight != null &&
              fromNav?.masterDepth != null
            ) {
              return fromNav;
            }
            const res = await getFinishedGoodById(fgId);
            return res.data;
          })
        );
        if (!cancelled) setLineFgOptions(loaded.filter(Boolean));
      } catch (e) {
        if (!cancelled) openSnack(handleError(e), "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [exportData?.exportItems, openSnack]);

  // Load container catalogue
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getCatalogue({ catalogueName: "EXPORT_CONTAINER_TYPE" });
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setContainerOptions(list);
        const preselectId = exportData?.containerTypeId;
        const fromList = list.find((c) => Number(c.id) === Number(preselectId));
        // Prefer catalogue list entry (has Props); fall back to export nav.
        const fromExport = fromList || exportData?.containerType || null;
        if (fromExport) setContainerType(fromExport);
      } catch (e) {
        openSnack(handleError(e), "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [exportData?.containerTypeId, exportData?.containerType, openSnack]);

  // When FG selected: ensure full dims (fetch if needed) and prefill quantity
  const applyFinishedGood = useCallback(
    async (fg) => {
      if (!fg) {
        setFinishedGood(null);
        setQuantity(0);
        setPackPlan(null);
        return;
      }
      setLoadingFg(true);
      try {
        let full = fg;
        const needsDims =
          full.masterWidth == null ||
          full.masterHeight == null ||
          full.masterDepth == null;
        if (needsDims || full.piecesPerMaster == null) {
          const res = await getFinishedGoodById(fg.id);
          full = res.data;
        }
        setFinishedGood(full);
        const masters = mastersFromExportItems(
          exportData?.exportItems,
          full.id,
          full.piecesPerMaster
        );
        setQuantity(masters > 0 ? masters : 0);
        setPackPlan(null);
      } catch (e) {
        openSnack(handleError(e), "error");
      } finally {
        setLoadingFg(false);
      }
    },
    [exportData?.exportItems, openSnack]
  );

  // Auto-select first FG from lines
  useEffect(() => {
    if (finishedGood) return;
    if (lineFgOptions.length === 1) {
      applyFinishedGood(lineFgOptions[0]);
    }
  }, [lineFgOptions, finishedGood, applyFinishedGood]);

  const containerProps = containerType?.props ?? containerType?.Props;

  const containerDimsLabel = useMemo(
    () => formatContainerDims(containerProps),
    [containerProps]
  );

  const handleGenerate = () => {
    const dims = containerDimsFromProps(containerProps);
    if (!dims) {
      openSnack(
        "El tipo de contenedor no tiene Props válidos {width, height, depth}",
        "error"
      );
      return;
    }
    if (!finishedGood) {
      openSnack("Seleccione un producto terminado", "error");
      return;
    }
    const qty = Math.max(0, Math.floor(Number(quantity) || 0));
    if (qty <= 0) {
      openSnack("Indique una cantidad de masters mayor a cero", "error");
      return;
    }

    const mw = Number(finishedGood.masterWidth);
    const mh = Number(finishedGood.masterHeight);
    const md = Number(finishedGood.masterDepth);
    if (![mw, mh, md].every((n) => Number.isFinite(n) && n > 0)) {
      openSnack("El producto terminado no tiene dimensiones de master válidas", "error");
      return;
    }

    try {
      // Match helperText: only explicit GRANEL/PALLET select those packers;
      // everything else (incl. FCL, GRANEL2, empty) uses GRANEL2.
      const packCode =
        shipmentCode === "PALLET"
          ? "PALLET"
          : shipmentCode === "GRANEL"
            ? "GRANEL"
            : "GRANEL2";
      const fromPallet = palletDimsFromProps(
        finishedGood.pallet?.props ?? finishedGood.pallet?.Props
      );
      const plan = pack(packCode, {
        masterWidth: mw,
        masterHeight: mh,
        masterDepth: md,
        palletWidth:
          fromPallet?.palletWidth || Number(finishedGood.palletWidth) || 0,
        palletHeight: Number(finishedGood.palletHeight) || 0,
        palletDepth:
          fromPallet?.palletDepth || Number(finishedGood.palletDepth) || 0,
        mastersPerLayer: Number(finishedGood.mastersPerLayer) || 0,
        layersPerPallet: Number(finishedGood.layersPerPallet) || 0,
        quantity: qty,
        contL: dims.contL,
        contW: dims.contW,
        contH: dims.contH,
      });
      setPackPlan(plan);
      setActiveContainer(0);
      const placed = plan.containers.reduce((s, c) => s + c.quantity, 0);
      openSnack(
        `Empaque generado: ${placed} unidades en ${plan.containers.length} contenedor(es)`,
        "success"
      );
    } catch (e) {
      openSnack(handleError(e), "error");
    }
  };

  const handleDownload = () => {
    const dataUrl = sceneRef.current?.capturePng?.();
    if (!dataUrl) {
      openSnack("Genere el diagrama antes de descargar", "warning");
      return;
    }
    const code = exportData?.code || "export";
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `empaque-${code}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const placedTotal = packPlan
    ? packPlan.containers.reduce((s, c) => s + c.quantity, 0)
    : 0;
  const containerCount = packPlan?.containers?.length || 0;

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          {lineFgOptions.length > 0 ? (
            <Autocomplete
              options={lineFgOptions}
              value={
                finishedGood
                  ? lineFgOptions.find((o) => o.id === finishedGood.id) ||
                    finishedGood
                  : null
              }
              getOptionLabel={(o) =>
                [o.code, o.name].filter(Boolean).join(" | ") || String(o.id)
              }
              isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
              onChange={(_, value) => applyFinishedGood(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Producto terminado (items)"
                  disabled={loadingFg}
                />
              )}
            />
          ) : (
            <LGAutocomplete
              label="Producto terminado"
              fetchFunction={getFinishedGoodList}
              fetchBy="search"
              queryParams={{ companyId, pageSize: 1000 }}
              optionLabel={["code", "name"]}
              optionSeparator=" | "
              value={finishedGood}
              onChange={(_, value) => applyFinishedGood(value)}
              disabled={loadingFg}
            />
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Autocomplete
            options={containerOptions}
            value={containerType}
            getOptionLabel={(o) =>
              [o.code, o.name].filter(Boolean).join(" | ") || String(o.id)
            }
            isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
            onChange={(_, value) => {
              setContainerType(value);
              setPackPlan(null);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Tipo de contenedor" />
            )}
          />
          <Typography variant="caption" color="text.secondary">
            {containerDimsLabel}
          </Typography>
        </Grid>

        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            type="number"
            label="Cantidad (masters)"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            label="Tipo de embarque"
            value={shipmentType?.name || shipmentCode || "—"}
            InputProps={{ readOnly: true }}
            helperText={
              shipmentCode === "PALLET"
                ? "Algoritmo PALLET"
                : shipmentCode === "GRANEL"
                  ? "Algoritmo GRANEL"
                  : "Algoritmo GRANEL2"
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Button
              variant="contained"
              onClick={handleGenerate}
              startIcon={<Icon>view_in_ar</Icon>}
            >
              Generar
            </Button>
            <Button
              variant="outlined"
              onClick={handleDownload}
              disabled={!packPlan}
              startIcon={<Icon>download</Icon>}
            >
              Descargar imagen
            </Button>
            {packPlan && (
              <Typography variant="body2" color="text.secondary">
                Colocados: {placedTotal} · Capacidad (suma):{" "}
                {packPlan.totalCapacity} · Contenedores: {containerCount}
              </Typography>
            )}
            {containerCount > 1 && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Contenedor</InputLabel>
                <Select
                  label="Contenedor"
                  value={activeContainer}
                  onChange={(e) => setActiveContainer(Number(e.target.value))}
                >
                  {packPlan.containers.map((c, i) => (
                    <MenuItem key={i} value={i}>
                      Contenedor {i + 1} ({c.quantity})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <EmpaqueScene
            ref={sceneRef}
            packPlan={packPlan}
            containerIndex={activeContainer}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
